import { createEffect, createSignal, Show, useContext } from 'solid-js';
import clsx from 'clsx';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { HeartCviIcon } from '../icons-library/heart-cvi';
import { HeartCviFilledIcon } from '../icons-library/heart-cvi-filled';
import { addMyContent, deleteMyContentItemByPermalink, getMyContent, MyContentItem } from './my-content-helpers';

export type CardFavouriteTarget = {
    title: string;
    permalink?: string;
    /** Stored as `type` on the My Content cookie entry. */
    type: string;
};

type Props = CardFavouriteTarget & {
    /** Positioning classes — the button owns its look, the caller owns its place. */
    class?: string;
};

/* The My Content heart as it sits on a card. Shared by EditorialCard and the slider
   card so the two can't drift; MyContentHandler is the older, separate implementation
   still used by the product and video listings. */
export const CardFavouriteButton = (props: Props) => {
    const { localize, siteInfo } = useContext(AppContext);
    const [isSaved, setIsSaved] = createSignal(false);

    const isEnabled = () => siteInfo.siteType === 'default';

    createEffect(() => {
        const permalink = props.permalink;

        if (!permalink) {
            setIsSaved(false);
            return;
        }

        const existing = (getMyContent() as MyContentItem[]).find((item) => item.permalink === permalink);
        setIsSaved(Boolean(existing));
    });

    const toggleSaved = (e: MouseEvent) => {
        /* The card's hit area is a sibling underneath this button, so stop the click
           here rather than letting it bubble into a navigation. */
        e.preventDefault();
        e.stopPropagation();

        const permalink = props.permalink || '/';

        if (isSaved()) {
            deleteMyContentItemByPermalink(permalink);
            setIsSaved(false);
            return;
        }

        addMyContent({
            title: props.title,
            permalink,
            type: props.type,
        });
        setIsSaved(true);
    };

    return (
        <Show when={isEnabled()}>
            <button
                type="button"
                /* Deliberately no `group-hover:` — the heart lights up only on its own
                   hover, not whenever the surrounding card is hovered. */
                class={clsx('icon-button-secondary-neutral icon-button-secondary-neutral-sm', props.class)}
                aria-label={
                    isSaved() ? localize('my_content_remove_star', 'Remove from My Content') : localize('save-to-my-content', 'Save to my content')
                }
                aria-pressed={isSaved()}
                onClick={toggleSaved}
            >
                <Show when={isSaved()} fallback={<HeartCviIcon size="small" />}>
                    <HeartCviFilledIcon size="small" />
                </Show>
            </button>
        </Show>
    );
};
